import { useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { motion, ease as mmEase } from "@/lib/motion";
import { CheckCircle2, Mail, ArrowRight, RefreshCw } from "lucide-react";
import Logo from "@/components/Logo";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

const ez = mmEase.expo;

export default function EmailVerification() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const emailParam = searchParams.get("email") || "";

  const [code, setCode]         = useState("");
  const [email, setEmail]       = useState(emailParam);
  const [loading, setLoading]   = useState(false);
  const [resending, setResend]  = useState(false);
  const [success, setSuccess]   = useState(false);
  const [error, setError]       = useState(null);

  async function handleVerify(e) {
    e.preventDefault();
    const trimmed = code.replace(/\s/g, "");
    if (trimmed.length !== 6) {
      setError("Please enter the 6-digit code from your email.");
      return;
    }
    setLoading(true); setError(null);
    try {
      await api.post("/auth/verify-email", { code: trimmed });
      await refresh();
      setSuccess(true);
      setTimeout(() => navigate("/app/dashboard"), 2200);
    } catch (err) {
      setError(err?.response?.data?.detail || "Invalid or expired code. Request a new one below.");
    } finally {
      setLoading(false);
    }
  }

  async function handleResend(e) {
    e.preventDefault();
    if (!email) { setError("Enter your email to resend the code."); return; }
    setResend(true);
    try {
      await api.post("/auth/verify-email/resend", { email: email.trim() });
      toast.success("New verification code sent — check your inbox.");
      setCode("");
    } catch (err) {
      toast.error(
        err?.response?.data?.detail ||
        "Could not send the verification email. Check your connection and try again."
      );
    } finally {
      setResend(false);
    }
  }

  return (
    <div className="min-h-screen hero-gradient grain flex flex-col items-center justify-center px-6 py-12 relative overflow-hidden">
      <div
        className="absolute top-1/4 left-1/2 -translate-x-1/2 w-[600px] h-[400px] pointer-events-none"
        style={{
          background: "radial-gradient(ellipse, rgba(16,185,129,0.12) 0%, transparent 65%)",
          filter: "blur(60px)",
        }}
      />

      <div className="relative z-10 flex flex-col items-center">
        <Link to="/" className="mb-10">
          <Logo variant="dark" size="md" />
        </Link>

        <div
          className="w-full max-w-[420px] rounded-2xl p-10"
          style={{
            background: "rgba(255,255,255,0.05)",
            border: "1px solid rgba(255,255,255,0.10)",
            backdropFilter: "blur(24px)",
          }}
        >
          {success ? (
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5, ease: ez }}
              className="text-center"
              data-testid="verify-success"
            >
              <div
                className="w-16 h-16 rounded-full grid place-items-center mx-auto mb-6"
                style={{ background: "rgba(16,185,129,0.15)", boxShadow: "0 8px 32px rgba(16,185,129,0.28)" }}
              >
                <CheckCircle2 size={28} className="text-emerald" strokeWidth={2} />
              </div>
              <h2
                className="font-display font-black text-white mb-3"
                style={{ fontSize: "1.5rem", letterSpacing: "-0.03em" }}
              >
                Email verified.
              </h2>
              <p className="text-white/50 text-[14px] mb-6">
                Your account is ready. Taking you to your dashboard…
              </p>
              <div className="w-full h-1 rounded-full bg-white/10 overflow-hidden">
                <motion.div
                  className="h-full rounded-full bg-emerald"
                  initial={{ width: "0%" }}
                  animate={{ width: "100%" }}
                  transition={{ duration: 2.0, ease: "linear" }}
                />
              </div>
            </motion.div>
          ) : (
            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, ease: ez }}
              data-testid="verify-no-token"
            >
              <div
                className="w-14 h-14 rounded-full grid place-items-center mx-auto mb-6"
                style={{ background: "rgba(16,185,129,0.10)", boxShadow: "0 8px 32px rgba(16,185,129,0.18)" }}
              >
                <Mail size={24} className="text-emerald" strokeWidth={1.5} />
              </div>

              <h2
                className="font-display font-black text-white mb-2 text-center"
                style={{ fontSize: "1.5rem", letterSpacing: "-0.03em" }}
              >
                Verify your email
              </h2>
              <p className="text-white/50 text-[13px] mb-7 text-center leading-relaxed">
                We sent a 6-digit code to your email. Enter it below to activate your account.
              </p>

              <form onSubmit={handleVerify} className="space-y-3">
                <div>
                  <label className="block text-[11px] font-semibold text-white/50 uppercase tracking-[0.14em] mb-1.5">
                    Verification code
                  </label>
                  <input
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    maxLength={6}
                    autoComplete="one-time-code"
                    autoFocus
                    required
                    value={code}
                    onChange={(e) => { setCode(e.target.value.replace(/[^0-9]/g, "")); setError(null); }}
                    placeholder="123456"
                    data-testid="verify-email-input"
                    className="w-full rounded-xl px-4 py-3.5 text-white text-center text-xl font-bold tracking-[0.4em] outline-none focus:ring-2 focus:ring-emerald/40 transition-all"
                    style={{ background: "rgba(255,255,255,0.07)", border: "1px solid rgba(255,255,255,0.14)" }}
                  />
                </div>

                {error && (
                  <motion.p
                    initial={{ opacity: 0, y: -4 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="text-[12px] font-medium text-red-300 text-center"
                    role="alert"
                  >
                    {error}
                  </motion.p>
                )}

                <button
                  type="submit"
                  disabled={loading || code.length < 6}
                  data-testid="verify-resend-button"
                  className="btn-primary w-full justify-center py-3 text-sm disabled:opacity-55"
                >
                  {loading ? (
                    <span className="flex items-center gap-2">
                      <span className="w-4 h-4 border-2 border-white/25 border-t-white rounded-full animate-spin" />
                      Verifying…
                    </span>
                  ) : (
                    <>Verify email <ArrowRight size={13} /></>
                  )}
                </button>
              </form>

              <div className="mt-6 pt-5 border-t border-white/10">
                <p className="text-[12px] text-white/40 text-center mb-3">Didn't receive a code?</p>
                <form onSubmit={handleResend} className="space-y-2">
                  <input
                    type="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="your@email.com"
                    className="w-full rounded-xl px-4 py-2.5 text-white text-sm outline-none focus:ring-2 focus:ring-emerald/30 transition-all"
                    style={{ background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.10)" }}
                  />
                  <button
                    type="submit"
                    disabled={resending}
                    className="w-full flex items-center justify-center gap-2 py-2.5 text-[12px] font-semibold text-white/70 hover:text-white transition-colors"
                  >
                    <RefreshCw size={12} className={resending ? "animate-spin" : ""} />
                    {resending ? "Sending…" : "Resend verification code"}
                  </button>
                </form>
              </div>
            </motion.div>
          )}
        </div>

        <p className="text-white/22 text-[11px] mt-8">
          © {new Date().getFullYear()} Mikkshebs System Technology S.R.L.
          {process.env.NODE_ENV !== "production" && " (dev)"}
        </p>
      </div>
    </div>
  );
}
